import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {ContractAccountMarginMode} from '@/enum';
import {max, min, plus, minus, gt, div, times, toString} from '@/helper/num';
import {calcUnrealisedPnlEv} from '@/calc';
import {contracts$, currenciesCurrencyCache$, contractCollateralsCache$, contractCollateralLiquility$} from '@/config';
import {indexPricesCache$} from '@/price-index/price-index';
import {contractAccountsCache$} from './contract-accounts-cache';
import {contractMarginMode$} from './contract-user';
import type {ws, CurrencyEntity, Position, ContractPair, ContractMarket} from '@/types';
import {positionsCache$} from '@/positions/positions-cache';
import {contractMarketRealtimeCache$} from '@/market-contract/market24h-contract-cache';
import {arr2Map} from '@/helper/structure-covert';
import {contractUtil} from '@/helper/contract';
type ContractAccountRaw = ws.ContractAccountRaw;
type ContractPositionRaw = ws.ContractPositionRaw;

export interface InnerContractAccount extends ContractAccountRaw {
  crossEquityEv?: string;
  crossAvailableEquityEv?: string;
  isPerpCollateral?: string;
  collateralCoefficient?: string;
  canBorrow?: string;
  indexPriceRp?: string;
  crossAvailableAsCollateralValueRv?: string;
  frozenAsCollateralEv?: string;
  remainingCrossAvailableAsCollateralValueRv?: string;
  occupationBalanceEv?: string;
  crossFixedUsedBalanceEv?: string;
  totalOrdUsedBalanceEv?: string;
  totalCrossPosCostEv?: string;
  valueFactor: number;
  totalPosMarginEv?: string;
}

export const innerContractAccounts$ = combineLatest([
  contractAccountsCache$,
  positionsCache$,
  indexPricesCache$,
  contractMarketRealtimeCache$,
  currenciesCurrencyCache$,
  contracts$,
  contractMarginMode$,
  contractCollateralsCache$,
  contractCollateralLiquility$,
]).pipe(
  map(
    ([
      accountsCache,
      positionsCache,
      indexPricesCache,
      contractMarketCache,
      currencies,
      contracts,
      contractMarginMode,
      contractCollateralsCache,
      contractCollateralLiquility,
    ]) => {
      const accounts = accountsCache.toArray<ContractAccountRaw>();
      const isMultiMarginMode = contractMarginMode && contractMarginMode === ContractAccountMarginMode.Multi;
      let accounts2;
      let totalCollateralAvaiValue = '0';
      let totalDebtValue = '0';
      accounts2 = accounts
        .map((account) =>
          calcCrossAvailableAsCollateralValueRv(
            isMultiMarginMode,
            account,
            positionsCache,
            contractMarketCache,
            currencies,
            contracts,
            contractCollateralsCache,
            indexPricesCache
          )
        )
        .filter(Boolean);
      totalCollateralAvaiValue = isMultiMarginMode
        ? accounts2.reduce((p, c) => plus(p, c.crossAvailableAsCollateralValueRv || 0), '0')
        : '0';
      totalDebtValue = isMultiMarginMode ? accounts2.reduce((p, c) => plus(p, c.occupationValue || 0), '0') : '0';
      if (gt(totalDebtValue, 0)) {
        const res = accounts2
          .sort((a, b) => {
            // 抵押占用顺序，按抵押品的流动性依次占用
            const aIndex = contractCollateralLiquility.indexOf(a.currency);
            const bIndex = contractCollateralLiquility.indexOf(b.currency);
            return aIndex - bIndex;
          })
          .reduce(
            ([debt, accounts3], account) => {
              const [newDebt, account2] = calcFrozenAsCollateral(debt, account);
              return [newDebt, [...accounts3, account2]];
            },
            [totalDebtValue, []]
          );
        accounts2 = res[1];
      }
      const totalCollateralAvaiValueRv = minus(totalCollateralAvaiValue, totalDebtValue);
      return {
        data: arr2Map(accounts2, 'currency') as Map<string, InnerContractAccount>,
        totalCollateralValueRv: totalCollateralAvaiValue, // 抵押品总价值
        totalCollateralAvaiValueRv0: totalCollateralAvaiValueRv, // 剩余抵押可借总价值
        totalCollateralAvaiValueRv: max(0, totalCollateralAvaiValueRv),
      };
    }
  )
);

function calcCrossAvailableAsCollateralValueRv(
  isMultiMarginMode: boolean,
  account: ContractAccountRaw,
  positionsCache: Map<string, ContractPositionRaw>,
  contractMarketCache: Map<string, ContractMarket>,
  currenciesCurrencyCache: Map<string, CurrencyEntity>,
  contracts: ContractPair[],
  contractCollateralsCache = null,
  indexPricesCache = null
) {
  const {
    currency,
    accountBalanceEv,
    totalUsedBalanceEv,
    bonusBalanceEv,
    isolatedTotalBalanceEv,
    totalDebtEv: borrowEv = '0',
    totalInterestEv: interestEv = '0',
  } = account;
  if (!currenciesCurrencyCache.has(currency)) {
    return null;
  }
  const collateralConfig = contractCollateralsCache.get(currency);
  // 跨资产占用金额
  let occupationValue = '0';
  // 可作为抵押的数量的金额
  let crossAvailableAsCollateralValueRv = '0';
  const indexPrice = indexPricesCache.get('.' + currency);
  const collateralCoefficient = collateralConfig
    ? times(1 - collateralConfig.reserveRatio || 0, collateralConfig.coefficient || 0)
    : '0';

  const positions = positionsCache.toArray<Position>();
  const relatedContracts = contracts.filter(({settleCurrency}) => settleCurrency === currency);
  const relatedPositions = positions.filter((o) => relatedContracts.some(({symbol}) => symbol === o.symbol));
  const unrealisedPnlEv = calcUnrealisedPnl(relatedPositions, contractMarketCache, contracts);
  const {valueFactor: valueFactor0} = currenciesCurrencyCache.get(currency);
  const valueFactor = contractUtil.formatterValueFactor(currency, valueFactor0);

  const debtEv = plus(borrowEv, interestEv);
  // CcyRiskPrinciple 每种 ccy 的本金
  const accountBalanceEv2 = Big(accountBalanceEv).minus(debtEv);
  // CcyRiskCrossEquity 每种 ccy 的全仓 equity = CcyRiskPrinciple - CcyTotalIsolatedPosUsedBalance + CcyTotalCrossUnrealisedPnl
  const crossEquityEv = accountBalanceEv2.minus(isolatedTotalBalanceEv).plus(unrealisedPnlEv).toString();

  /**
   * 全仓可用 equity，可能为负
   * crossAvailableEquity = CcyRiskPrinciple - totalUsedBalanceEv
   * totalUsedBalanceEv = totalCrossPosCost + totalCrossFixedUsedBalance + totalOrderUsedBalance + isolatedTotalBalanceEv + frozenAsCollateralEv
   */
  const crossAvailableEquity = accountBalanceEv2
    .minus(isolatedTotalBalanceEv)
    .plus(unrealisedPnlEv)
    .minus(totalUsedBalanceEv);
  const occupationBalanceEv =
    isMultiMarginMode && collateralConfig && collateralConfig.canBorrow && gt(0, crossAvailableEquity)
      ? Big(crossAvailableEquity).abs()
      : Big(0);

  if (isMultiMarginMode && indexPrice) {
    // 联合保证金模式，且支持抵押
    // CcyRiskFrozenAsCollateral 目前可以用于抵押的数目
    const availableAsCollateral =
      collateralConfig && currency !== 'USDT'
        ? max(0, min(accountBalanceEv2.minus(bonusBalanceEv), crossAvailableEquity))
        : '0';

    if (collateralConfig && collateralConfig.isPerpCollateral) {
      // 每种 ccy 可作为抵押的数目，转化为 USDT 价值：crossAvailableEquity * indexPrice * (1 - CollateralReserveRatio) * CollateralRate
      crossAvailableAsCollateralValueRv = Big(availableAsCollateral)
        .times(indexPrice.priceEp)
        .times(collateralCoefficient)
        .div(valueFactor)
        .div(indexPrice.priceFactor)
        .toString();
    }

    occupationValue = gt(occupationBalanceEv, 0)
      ? occupationBalanceEv.times(indexPrice.priceEp).div(valueFactor).div(indexPrice.priceFactor).toString()
      : '0';
  }

  return {
    ...account,
    canBorrow: isMultiMarginMode && collateralConfig ? collateralConfig.canBorrow : false,
    isPerpCollateral: isMultiMarginMode && collateralConfig ? collateralConfig.isPerpCollateral : false,
    crossEquityEv,
    crossAvailableEquityEv: toString(crossAvailableEquity),
    collateralCoefficient,
    occupationBalanceEv: toString(occupationBalanceEv), // 联合保证金模式，支持抵押开仓，超出本金占用数量
    occupationValue, // 联合保证金模式，支持抵押开仓，超出本金占用的金额
    indexPriceRp: indexPrice ? div(indexPrice.priceEp, indexPrice.priceFactor) : '0',
    crossAvailableAsCollateralValueRv,
    valueFactor,
  };
}

function calcFrozenAsCollateral(debt: string, account) {
  let newDebt = debt;
  let collateralVal = '0';
  let remainingCrossAvailableAsCollateralValueRv = '0';
  if (gt(account.crossAvailableAsCollateralValueRv, 0) && gt(debt, 0)) {
    if (gt(debt, account.crossAvailableAsCollateralValueRv)) {
      newDebt = minus(debt, account.crossAvailableAsCollateralValueRv);
      collateralVal = account.crossAvailableAsCollateralValueRv;
      remainingCrossAvailableAsCollateralValueRv = '0';
    } else {
      newDebt = '0';
      collateralVal = debt;
      remainingCrossAvailableAsCollateralValueRv = minus(account.crossAvailableAsCollateralValueRv, debt);
    }
  }
  delete account.occupationValue;
  const frozenAsCollateralRv = gt(collateralVal, 0)
    ? div(div(collateralVal, account.collateralCoefficient), account.indexPriceRp)
    : 0;
  return [
    newDebt,
    {
      frozenAsCollateralEv: times(frozenAsCollateralRv, account.valueFactor),
      remainingCrossAvailableAsCollateralValueRv,
      ...account,
    },
  ];
}

function calcUnrealisedPnl(
  positions: Position[],
  contractMarketCache: Map<string, ContractMarket>,
  contracts: ContractPair[]
) {
  return positions.reduce((p, c) => {
    let pnl = 0;
    const market = contractMarketCache.get(c.symbol);
    const contract = contracts.find((o) => o.symbol === c.symbol);
    const markPriceEp = market ? market.markPriceEp : 0;
    if (contract) {
      const {contractSide, contractSize, valueFactor, priceFactor} = contract;
      pnl = calcUnrealisedPnlEv({
        side: c.side,
        contractSide,
        contractSize,
        qty: c.size,
        entryPriceEp: c.avgEntryPriceEp,
        exitPriceEp: markPriceEp,
        factor: valueFactor,
        priceFactor,
      });
    }
    return plus(p, pnl);
  }, '0');
}
